/* global subscribe, PUB_SUB_EVENTS */

if (!customElements.get('product-sticky-buy')) {
  customElements.define(
    'product-sticky-buy',
    class ProductStickyBuy extends HTMLElement {
      connectedCallback() {
        this.productInfo = this.closest('product-info');
        if (!this.productInfo) return;

        this.toggleButton = this.querySelector('[data-sticky-action="toggle"]');
        this.mobilePrimary = this.querySelector('[data-sticky-action="mobile-primary"]');
        this.panel = this.querySelector('[data-sticky-panel]');
        this.tierList = this.querySelector('[data-sticky-tier-list]');
        this.tierStatus = this.querySelector('[data-sticky-tier-status]');
        this.submitProxy = this.querySelector('[data-sticky-action="submit"]');
        this.submitProxyLabel = this.querySelector('[data-sticky-submit-label]');
        this.mobileSubmitAction = this.querySelector('[data-sticky-mobile-action]');
        this.mobileSubmitDetails = this.querySelector('[data-sticky-mobile-details]');
        this.mobilePriceValue = this.querySelector('[data-sticky-mobile-price]');
        this.decreaseProxy = this.querySelector('[data-sticky-action="decrease"]');
        this.increaseProxy = this.querySelector('[data-sticky-action="increase"]');
        this.quantityValue = this.querySelector('[data-sticky-quantity]');
        this.quantityControls = this.querySelector('[data-sticky-quantity-controls]');
        this.handlePrefixValue = this.querySelector('[data-sticky-handle-prefix]');
        this.handlePriceValue = this.querySelector('[data-sticky-handle-price]');
        this.priceValue = this.querySelector('[data-sticky-price]');
        this.comparePriceValue = this.querySelector('[data-sticky-compare-price]');
        this.errorValue = this.querySelector('[data-sticky-error]');
        this.desktopQuery = window.matchMedia('(min-width: 1280px)');
        this.mobileQuery = window.matchMedia('(max-width: 989px)');
        this.stickyHeader = document.querySelector('sticky-header');
        this.headerSection = this.stickyHeader?.closest('.section-header');
        this.eventController = new AbortController();
        this.canonicalSubmitPassed = false;

        const eventOptions = { signal: this.eventController.signal };
        this.addEventListener('click', this.handleClick.bind(this), eventOptions);
        this.addEventListener('pointerenter', this.handlePointerEnter.bind(this), eventOptions);
        this.addEventListener('pointerleave', this.handlePointerLeave.bind(this), eventOptions);
        this.addEventListener('focusin', this.handleFocusIn.bind(this), eventOptions);
        this.addEventListener('focusout', this.handleFocusOut.bind(this), eventOptions);
        this.productInfo.addEventListener('change', this.handleCanonicalInput.bind(this), eventOptions);
        this.productInfo.addEventListener('input', this.handleCanonicalInput.bind(this), eventOptions);
        this.productInfo.addEventListener('product-section-nav:toggle', this.handleSectionNavToggle.bind(this), eventOptions);
        document.addEventListener('pointerdown', this.handleOutsidePointer.bind(this), eventOptions);
        document.addEventListener('keydown', this.handleKeyDown.bind(this), eventOptions);
        window.addEventListener('scroll', this.schedulePositionCheck.bind(this), {
          passive: true,
          signal: this.eventController.signal,
        });
        window.addEventListener('resize', this.schedulePositionCheck.bind(this), eventOptions);
        window.visualViewport?.addEventListener('resize', this.schedulePositionCheck.bind(this), eventOptions);
        window.visualViewport?.addEventListener('scroll', this.schedulePositionCheck.bind(this), eventOptions);
        this.headerSection?.addEventListener('transitionend', this.schedulePositionCheck.bind(this), eventOptions);

        if (this.headerSection) {
          this.headerStateObserver = new MutationObserver(() => this.schedulePositionCheck());
          this.headerStateObserver.observe(this.headerSection, { attributes: true, attributeFilter: ['class'] });
        }

        this.handleViewportChange = () => {
          const sectionNav = this.productInfo.querySelector('product-section-nav');
          this.updateSectionNavCoordination(sectionNav?.dataset.menuOpen === 'true');
          if (this.mobileQuery?.matches && !this.hasQuantityTiers()) {
            this.setExpanded(false);
          } else {
            this.updatePanelState();
            this.updateMobilePrimarySemantics();
          }
          this.updateStickyTop();
          this.updateCanonicalPosition();
        };
        [this.desktopQuery, this.mobileQuery].forEach((query) => {
          if (query.addEventListener) {
            query.addEventListener('change', this.handleViewportChange);
          } else {
            query.addListener(this.handleViewportChange);
          }
        });

        this.refreshCanonicalReferences();
        this.subscribeToProductEvents();
        this.syncFromCanonical();
      }

      disconnectedCallback() {
        if (this.dataset.expanded === 'true') this.notifyExpandedChange(false);
        this.eventController?.abort();
        this.stateObserver?.disconnect();
        this.submitObserver?.disconnect();
        this.headerStateObserver?.disconnect();
        this.variantChangeUnsubscriber?.();
        this.quantityUpdateUnsubscriber?.();
        this.cartUpdateUnsubscriber?.();
        [this.desktopQuery, this.mobileQuery].filter(Boolean).forEach((query) => {
          if (!this.handleViewportChange) return;
          if (query.removeEventListener) {
            query.removeEventListener('change', this.handleViewportChange);
          } else {
            query.removeListener(this.handleViewportChange);
          }
        });
        if (this.syncFrame) window.cancelAnimationFrame(this.syncFrame);
        if (this.positionFrame) window.cancelAnimationFrame(this.positionFrame);
      }

      subscribeToProductEvents() {
        if (typeof subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;

        this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event = {}) => {
          const sectionId = String(event.data?.sectionId || '');
          if (sectionId && sectionId !== String(this.productInfo.dataset.section || '')) return;
          this.syncTierMarkup(event.data?.html);
          this.scheduleSync(true);
        });
        this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, () => this.scheduleSync());
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, () => this.scheduleSync());
      }

      get stickyTierButtons() {
        return Array.from(this.tierList?.querySelectorAll?.('[data-sticky-tier]') || []);
      }

      get canonicalTierButtons() {
        return Array.from(this.canonicalTierRoot?.querySelectorAll?.('[data-quantity-tier]') || []);
      }

      hasQuantityTiers() {
        if (this.dataset.hasQuantityTiers !== undefined) {
          return this.dataset.hasQuantityTiers === 'true';
        }
        return this.stickyTierButtons.length > 0;
      }

      syncTierMarkup(html) {
        if (!this.tierList || !html?.querySelector) return;
        const sourceList = html.querySelector('product-sticky-buy [data-sticky-tier-list]');
        if (!sourceList) return;

        const focusedTier = this.tierList.contains?.(document.activeElement);
        this.tierList.innerHTML = sourceList.innerHTML;
        const tierCount = this.stickyTierButtons.length;
        this.setHasQuantityTiers(Boolean(tierCount && this.quantityInput), tierCount);
        if (focusedTier && !this.hasQuantityTiers()) {
          this.mobilePrimary?.focus({ preventScroll: true });
        }
      }

      setHasQuantityTiers(hasTiers, tierCount = this.stickyTierButtons.length) {
        this.dataset.hasQuantityTiers = hasTiers ? 'true' : 'false';
        this.dataset.quantityTierCount = String(hasTiers ? Math.max(1, this.toInteger(tierCount, 1)) : 0);
        if (!hasTiers && this.mobileQuery?.matches) this.setExpanded(false);
        this.updatePanelState();
      }

      refreshCanonicalReferences() {
        const previousSubmit = this.canonicalSubmit;
        this.canonicalForm = this.productInfo.querySelector('form[data-type="add-to-cart-form"]');
        this.canonicalProductForm = this.canonicalForm?.closest('product-form');
        this.canonicalSubmit = this.canonicalForm?.querySelector('.product-form__submit');
        this.quantityInput = this.productInfo.querySelector('.product-form__quantity .quantity__input');
        this.priceRoot = this.productInfo.querySelector('[id^="price-"]');
        this.pricePerItem = this.productInfo.querySelector('price-per-item[id^="Price-Per-Item-"]');
        this.canonicalTierRoot = this.productInfo.querySelector('volume-pricing[id^="Volume-"]');
        this.canonicalError = this.canonicalProductForm?.querySelector('.product-form__error-message-wrapper');

        this.observeCanonicalState();
        if (previousSubmit !== this.canonicalSubmit) this.observeCanonicalSubmit();

        if (!this.canonicalForm || !this.canonicalSubmit) {
          this.hidden = true;
          this.setAttribute('aria-hidden', 'true');
          return false;
        }
        return true;
      }

      observeCanonicalState() {
        this.stateObserver?.disconnect();
        this.stateObserver = new MutationObserver(() => this.scheduleSync());
        [
          this.canonicalSubmit,
          this.quantityInput,
          this.priceRoot,
          this.pricePerItem,
          this.canonicalTierRoot,
          this.canonicalError,
        ]
          .filter(Boolean)
          .forEach((node) => this.stateObserver.observe(node, {
            attributes: true,
            childList: true,
            characterData: true,
            subtree: true,
          }));
      }

      observeCanonicalSubmit() {
        this.submitObserver?.disconnect();
        this.canonicalSubmitPassed = false;
        if (!this.canonicalSubmit) return this.updateVisibility();

        this.updateCanonicalPosition();

        if ('IntersectionObserver' in window) {
          this.submitObserver = new IntersectionObserver((entries) => {
            const entry = entries.find(({ target }) => target === this.canonicalSubmit);
            if (!entry) return;
            this.updateCanonicalPosition();
          });
          this.submitObserver.observe(this.canonicalSubmit);
        }
      }

      handleClick(event) {
        const control = event.target.closest('[data-sticky-action]');
        if (!control || !this.contains(control)) return;
        const action = control.dataset.stickyAction;

        if (action === 'toggle') {
          const shouldExpand = this.dataset.expanded !== 'true';
          if (shouldExpand) {
            delete this.dataset.forceCollapsed;
          } else {
            this.dataset.forceCollapsed = 'true';
          }
          this.setExpanded(shouldExpand);
          return;
        }
        if (action === 'mobile-primary') {
          if (this.hasQuantityTiers()) {
            this.setExpanded(this.dataset.expanded !== 'true');
          } else if (!this.mobilePrimary.disabled) {
            this.submitCanonicalForm();
          }
          return;
        }
        if (action === 'tier') {
          this.proxyQuantityTier(control);
          return;
        }
        if (action === 'decrease' || action === 'increase') {
          this.proxyQuantityAction(action, control);
          return;
        }
        if (action === 'submit' && !this.submitProxy.disabled) {
          this.submitCanonicalForm();
        }
      }

      submitCanonicalForm() {
        this.canonicalForm?.requestSubmit(this.canonicalSubmit);
        this.scheduleSync();
      }

      proxyQuantityAction(action, control) {
        if (control.disabled || !this.quantityInput) return;
        const canonicalAction = action === 'increase' ? 'plus' : 'minus';
        const quantityButton = this.quantityInput
          .closest('quantity-input')
          ?.querySelector(`.quantity__button[name="${canonicalAction}"]`);
        quantityButton?.click();
        this.scheduleSync();
      }

      proxyQuantityTier(control) {
        if (control.disabled || !this.quantityInput) return;
        const threshold = this.toInteger(control.dataset.stickyTier);
        if (!threshold) return;

        const canonicalButton = this.canonicalTierButtons.find(
          (button) => this.toInteger(button.dataset.quantityTier) === threshold
        );
        if (canonicalButton) {
          if (canonicalButton.disabled) return;
          canonicalButton.click();
          this.announceTierSelection(control, threshold);
          this.scheduleSync();
          return;
        }

        const cartQuantity = this.toInteger(this.quantityInput.dataset.cartQuantity, 0);
        const minimum = this.toInteger(this.quantityInput.min || this.quantityInput.dataset.min, 1);
        const increment = this.toInteger(this.quantityInput.step, 1);
        const maximum = this.toInteger(this.quantityInput.max, null);
        const additionalNeeded = Math.max(minimum, threshold - cartQuantity);
        let nextValue = this.alignToIncrement(additionalNeeded, minimum, increment);

        if (maximum !== null) nextValue = Math.min(nextValue, maximum);
        if (cartQuantity + nextValue < threshold) return;

        this.quantityInput.value = String(nextValue);
        this.quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
        this.announceTierSelection(control, threshold);
        this.scheduleSync();
      }

      announceTierSelection(control, threshold) {
        if (!this.tierStatus) return;
        const price = control.querySelector?.('.product-sticky-buy__mobile-tier-price')?.textContent?.trim();
        this.tierStatus.textContent = `${threshold} flasker valgt${price ? `. ${price}` : ''}`;
      }

      alignToIncrement(quantity, minimum, increment) {
        const safeIncrement = Math.max(1, increment);
        return minimum + Math.ceil(Math.max(0, quantity - minimum) / safeIncrement) * safeIncrement;
      }

      toInteger(value, fallback = null) {
        const parsed = Number.parseInt(value, 10);
        return Number.isFinite(parsed) ? parsed : fallback;
      }

      handleCanonicalInput(event) {
        if (event.target === this.quantityInput) this.scheduleSync();
      }

      handleOutsidePointer(event) {
        if (!this.contains(event.target)) {
          delete this.dataset.forceCollapsed;
          this.setExpanded(false);
        }
      }

      handlePointerEnter(event) {
        if (event.pointerType !== 'mouse') return;
        if (this.mobileQuery?.matches) return;
        const focusedSectionNav = document.activeElement?.closest?.('product-section-nav');
        if (focusedSectionNav?.dataset.menuOpen === 'true') return;
        if (this.dataset.forceCollapsed === 'true') return;
        if (this.dataset.expanded !== 'true') {
          const handleBounds = this.toggleButton?.getBoundingClientRect?.();
          this.hoverAnchorBounds = handleBounds
            ? {
                bottom: handleBounds.bottom,
                left: handleBounds.left,
                right: handleBounds.right,
                top: handleBounds.top,
              }
            : null;
        }
        this.setExpanded(true);
      }

      handlePointerLeave(event) {
        if (event.pointerType !== 'mouse') return;
        if (this.mobileQuery?.matches) return;
        if (this.matches(':focus-within')) return;
        const wasExpanded = this.dataset.expanded === 'true';
        const anchor = this.hoverAnchorBounds;
        const leftAtAnchor = Boolean(
          wasExpanded
          && anchor
          && event.clientX >= anchor.left
          && event.clientX <= anchor.right
          && event.clientY >= anchor.top
          && event.clientY <= anchor.bottom
        );
        if (leftAtAnchor) {
          this.dataset.forceCollapsed = 'true';
        } else {
          delete this.dataset.forceCollapsed;
          this.hoverAnchorBounds = null;
        }
        this.setExpanded(false);
      }

      handleFocusIn(event) {
        if (!event.target.matches?.(':focus-visible')) return;
        if (this.mobileQuery?.matches) return;
        if (this.dataset.forceCollapsed !== 'true') this.setExpanded(true);
      }

      handleFocusOut(event) {
        if (!this.contains(event.relatedTarget)) {
          delete this.dataset.forceCollapsed;
          this.setExpanded(false);
          this.updateVisibility();
        }
      }

      handleKeyDown(event) {
        if (event.key !== 'Escape' || this.hidden) return;
        if (this.dataset.expanded !== 'true' && !this.matches(':focus-within')) return;
        event.preventDefault();
        this.setExpanded(false);
        if (this.mobileQuery?.matches) {
          delete this.dataset.forceCollapsed;
          this.mobilePrimary?.focus({ preventScroll: true });
        } else {
          this.dataset.forceCollapsed = 'true';
          this.toggleButton?.focus({ preventScroll: true });
        }
      }

      handleSectionNavToggle(event) {
        this.updateSectionNavCoordination(event.detail?.expanded === true);
      }

      updateSectionNavCoordination(expanded) {
        this.sectionNavExpanded = expanded && !this.mobileQuery?.matches;
        if (this.sectionNavExpanded) {
          this.dataset.forceCollapsed = 'true';
          this.setExpanded(false);
        } else {
          delete this.dataset.forceCollapsed;
        }
        this.updateVisibility();
      }

      setExpanded(expanded) {
        const canExpand = !this.mobileQuery?.matches || this.hasQuantityTiers();
        const nextExpanded = Boolean(expanded && canExpand);
        const wasExpanded = this.dataset.expanded === 'true';
        if (nextExpanded) delete this.dataset.forceCollapsed;
        this.dataset.expanded = nextExpanded ? 'true' : 'false';
        this.toggleButton?.setAttribute('aria-expanded', nextExpanded ? 'true' : 'false');
        this.updatePanelState();
        this.updateToggleLabel(nextExpanded);
        this.updateMobilePrimarySemantics();
        if (wasExpanded !== nextExpanded) this.notifyExpandedChange(nextExpanded);
      }

      updatePanelState() {
        if (!this.panel) return;
        const expanded = this.dataset.expanded === 'true';
        const mobile = Boolean(this.mobileQuery?.matches);
        const hasVisibleError = this.dataset.errorVisible === 'true';
        const mobilePanelVisible = (expanded && this.hasQuantityTiers()) || hasVisibleError;
        const panelVisible = mobile ? mobilePanelVisible : expanded;

        this.panel.hidden = !panelVisible;
        this.panel.inert = !panelVisible;
        this.panel.setAttribute('aria-hidden', panelVisible ? 'false' : 'true');
      }

      notifyExpandedChange(expanded) {
        this.dispatchEvent(new CustomEvent('product-sticky-buy:toggle', {
          bubbles: true,
          detail: { expanded },
        }));
      }

      updateToggleLabel(expanded = this.dataset.expanded === 'true', handleOffer = this.getHandleOffer()) {
        if (!this.toggleButton) return;
        const action = handleOffer.isFrom ? `Køb fra ${handleOffer.priceText}` : `Køb ${handleOffer.priceText}`;
        this.toggleButton.setAttribute(
          'aria-label',
          `${expanded ? 'Luk' : 'Åbn'} hurtig køb – ${action}`
        );
      }

      updateMobilePrimarySemantics({
        quantity = Number.parseInt(this.quantityInput?.value, 10) || 1,
        canonicalLoading = Boolean(this.canonicalSubmit?.classList?.contains('loading')),
        canonicalDisabled = Boolean(
          this.canonicalSubmit?.disabled || this.canonicalSubmit?.getAttribute?.('aria-disabled') === 'true'
        ),
        canonicalLabel = this.canonicalSubmit?.querySelector?.('span')?.textContent?.trim(),
        priceText = this.getCanonicalPriceText(),
        handleOffer = this.getHandleOffer(priceText),
      } = {}) {
        if (!this.mobilePrimary) return;

        const hasTiers = this.hasQuantityTiers();
        const expanded = this.dataset.expanded === 'true';
        const bottleLabel = quantity === 1 ? '1 flaske' : `${quantity} flasker`;
        this.mobilePrimary.disabled = hasTiers ? false : canonicalDisabled;
        this.mobilePrimary.classList?.toggle('loading', canonicalLoading);
        this.mobilePrimary.setAttribute('aria-busy', canonicalLoading ? 'true' : 'false');

        if (hasTiers) {
          this.mobilePrimary.setAttribute('aria-expanded', expanded ? 'true' : 'false');
          if (this.panel?.id) this.mobilePrimary.setAttribute('aria-controls', this.panel.id);
          this.mobilePrimary.removeAttribute?.('aria-haspopup');
          const offer = handleOffer.priceText
            ? `${handleOffer.isFrom ? 'fra ' : ''}${handleOffer.priceText}`
            : priceText;
          const context = canonicalLoading
            ? `Tilføjer ${bottleLabel}…`
            : canonicalDisabled
              ? canonicalLabel || 'Ikke tilgængelig'
              : offer
                ? `Køb ${offer}`
                : '';
          this.mobilePrimary.setAttribute(
            'aria-label',
            `${expanded ? 'Luk' : 'Vis'} mængdepriser${context ? ` – ${context}` : ''}`
          );
          return;
        }

        this.mobilePrimary.removeAttribute?.('aria-expanded');
        this.mobilePrimary.removeAttribute?.('aria-controls');
        const popupType = this.canonicalSubmit?.getAttribute?.('aria-haspopup');
        if (popupType === 'dialog') {
          this.mobilePrimary.setAttribute('aria-haspopup', 'dialog');
        } else {
          this.mobilePrimary.removeAttribute?.('aria-haspopup');
        }
        const accessibleLabel = canonicalLoading
          ? `Tilføjer ${bottleLabel}…`
          : canonicalDisabled
            ? canonicalLabel || 'Ikke tilgængelig'
            : priceText
              ? `Køb ${bottleLabel} til ${priceText} pr. flaske`
              : `Køb ${bottleLabel}`;
        this.mobilePrimary.setAttribute('aria-label', accessibleLabel);
      }

      scheduleSync(refreshReferences = false) {
        this.shouldRefreshReferences = this.shouldRefreshReferences || refreshReferences;
        if (this.syncFrame) return;
        this.syncFrame = window.requestAnimationFrame(() => {
          this.syncFrame = null;
          if (this.shouldRefreshReferences) {
            this.shouldRefreshReferences = false;
            this.refreshCanonicalReferences();
          }
          this.syncFromCanonical();
        });
      }

      schedulePositionCheck() {
        if (this.positionFrame) return;
        this.positionFrame = window.requestAnimationFrame(() => {
          this.positionFrame = null;
          this.updateCanonicalPosition();
        });
      }

      updateCanonicalPosition() {
        this.updateStickyTop();
        if (!this.canonicalSubmit) {
          this.canonicalSubmitPassed = false;
        } else {
          const stickyTop = this.mobileQuery?.matches ? this.getStickyTop() : 0;
          this.canonicalSubmitPassed = this.canonicalSubmit.getBoundingClientRect().bottom <= stickyTop;
        }
        this.updateVisibility();
      }

      getStickyTop() {
        if (!this.mobileQuery?.matches) return 0;
        const viewportTop = window.visualViewport?.offsetTop || 0;
        const headerBottom = this.stickyHeader?.getBoundingClientRect?.().bottom;
        return Number.isFinite(headerBottom)
          ? Math.max(viewportTop, Math.round(headerBottom))
          : viewportTop;
      }

      updateStickyTop() {
        const stickyTop = this.getStickyTop();
        this.style?.setProperty('--product-sticky-buy-top', `${stickyTop}px`);
        this.closest('[data-product-side-rail]')?.style?.setProperty(
          '--product-side-rail-mobile-top',
          `${stickyTop}px`
        );
      }

      syncFromCanonical() {
        if (!this.canonicalForm || !this.canonicalSubmit) return this.updateVisibility();

        const quantity = Number.parseInt(this.quantityInput?.value, 10) || 1;
        const minimum = Number.parseInt(this.quantityInput?.min || this.quantityInput?.dataset.min, 10) || 1;
        const maximumValue = Number.parseInt(this.quantityInput?.max || this.quantityInput?.dataset.max, 10);
        const maximum = Number.isFinite(maximumValue) ? maximumValue : null;
        const canonicalLoading = this.canonicalSubmit.classList.contains('loading');
        const canonicalDisabled = this.canonicalSubmit.disabled
          || this.canonicalSubmit.getAttribute('aria-disabled') === 'true';
        const canonicalLabel = this.canonicalSubmit.querySelector('span')?.textContent.trim();
        const priceText = this.getCanonicalPriceText();
        const bottleLabel = quantity === 1 ? '1 flaske' : `${quantity} flasker`;
        const tierCount = this.stickyTierButtons.length;
        const hasQuantityTiers = tierCount > 0 && Boolean(this.quantityInput);

        if (
          (this.dataset.hasQuantityTiers === 'true') !== hasQuantityTiers
          || this.toInteger(this.dataset.quantityTierCount, 0) !== tierCount
        ) {
          this.setHasQuantityTiers(hasQuantityTiers, tierCount);
        }
        this.syncTierStates(canonicalDisabled);

        if (this.quantityControls) this.quantityControls.hidden = !this.quantityInput;
        if (this.quantityValue) this.quantityValue.textContent = String(quantity);
        if (this.mobilePriceValue) this.mobilePriceValue.textContent = priceText;
        if (this.decreaseProxy) this.decreaseProxy.disabled = canonicalDisabled || quantity <= minimum;
        if (this.increaseProxy) this.increaseProxy.disabled = canonicalDisabled || (maximum !== null && quantity >= maximum);

        if (this.submitProxy) {
          this.submitProxy.disabled = canonicalDisabled;
          this.submitProxy.classList.toggle('loading', canonicalLoading);
          this.submitProxy.setAttribute('aria-busy', canonicalLoading ? 'true' : 'false');
          const popupType = this.canonicalSubmit.getAttribute('aria-haspopup');
          if (popupType === 'dialog') {
            this.submitProxy.setAttribute('aria-haspopup', 'dialog');
          } else {
            this.submitProxy.removeAttribute?.('aria-haspopup');
          }
          const accessibleSubmitLabel = canonicalLoading
            ? `Tilføjer ${bottleLabel}…`
            : canonicalDisabled
              ? canonicalLabel || 'Ikke tilgængelig'
              : priceText
                ? `Køb ${bottleLabel} til ${priceText} pr. flaske`
                : `Køb ${bottleLabel}`;
          this.submitProxy.setAttribute(
            'aria-label',
            accessibleSubmitLabel
          );
        }
        if (this.submitProxyLabel) {
          this.submitProxyLabel.textContent = canonicalLoading ? 'Tilføjer…' : canonicalLabel || 'Læg i kurv';
        }
        if (this.mobileSubmitAction) {
          this.mobileSubmitAction.textContent = canonicalLoading
            ? 'Tilføjer…'
            : canonicalDisabled
              ? canonicalLabel || 'Ikke tilgængelig'
              : 'Køb';
        }
        if (this.mobileSubmitDetails) this.mobileSubmitDetails.hidden = canonicalLoading || canonicalDisabled;
        if (priceText) {
          if (this.priceValue) this.priceValue.textContent = priceText;
        }
        const comparePriceText = this.getCanonicalComparePriceText();
        if (this.comparePriceValue) {
          this.comparePriceValue.textContent = comparePriceText;
          this.comparePriceValue.hidden = !comparePriceText;
        }
        const handleOffer = this.getHandleOffer(priceText);
        if (this.handlePriceValue && handleOffer.priceText) {
          this.handlePriceValue.textContent = handleOffer.priceText;
        }
        if (this.handlePrefixValue) this.handlePrefixValue.hidden = !handleOffer.isFrom;
        this.updateToggleLabel(this.dataset.expanded === 'true', handleOffer);
        this.updateMobilePrimarySemantics({
          quantity,
          canonicalLoading,
          canonicalDisabled,
          canonicalLabel,
          priceText,
          handleOffer,
        });

        const errorText = this.canonicalError && !this.canonicalError.hidden
          ? this.canonicalError.textContent.trim().replace(/\s+/g, ' ')
          : '';
        if (this.errorValue) {
          this.errorValue.textContent = errorText;
          this.errorValue.hidden = !errorText;
        }
        [this.mobilePrimary, this.submitProxy].filter(Boolean).forEach((control) => {
          if (errorText && this.errorValue?.id) {
            control.setAttribute('aria-describedby', this.errorValue.id);
          } else {
            control.removeAttribute?.('aria-describedby');
          }
        });
        this.dataset.errorVisible = errorText ? 'true' : 'false';
        if (errorText && (!this.mobileQuery?.matches || this.hasQuantityTiers())) this.setExpanded(true);

        this.updatePanelState();
        this.updateVisibility();
      }

      syncTierStates(canonicalDisabled = false) {
        const canonicalButtons = this.canonicalTierButtons;
        if (!this.quantityInput) {
          this.stickyTierButtons.forEach((button) => {
            button.disabled = true;
            button.setAttribute('aria-pressed', 'false');
          });
          return;
        }
        const cartQuantity = this.toInteger(this.quantityInput?.dataset?.cartQuantity, 0);
        const enteredQuantity = this.toInteger(this.quantityInput?.value, 1);
        const totalQuantity = cartQuantity + enteredQuantity;
        const minimum = this.toInteger(this.quantityInput?.min || this.quantityInput?.dataset?.min, 1);
        const increment = this.toInteger(this.quantityInput?.step, 1);
        const maximum = this.toInteger(this.quantityInput?.max, null);
        let activeFallback = null;

        this.stickyTierButtons.forEach((button) => {
          const threshold = this.toInteger(button.dataset.stickyTier);
          const canonicalButton = canonicalButtons.find(
            (candidate) => this.toInteger(candidate.dataset.quantityTier) === threshold
          );
          if (canonicalButton) {
            button.disabled = canonicalButton.disabled;
            button.setAttribute('aria-pressed', canonicalButton.getAttribute('aria-pressed') === 'true' ? 'true' : 'false');
            return;
          }

          const additionalNeeded = Math.max(minimum, threshold - cartQuantity);
          const alignedQuantity = this.alignToIncrement(additionalNeeded, minimum, increment);
          const cannotReachTier = maximum !== null && cartQuantity < threshold && alignedQuantity > maximum;
          button.disabled = canonicalDisabled || cannotReachTier;
          button.setAttribute('aria-pressed', 'false');
          if (!button.disabled && threshold <= totalQuantity) activeFallback = button;
        });

        if (!canonicalButtons.length) activeFallback?.setAttribute('aria-pressed', 'true');
      }

      getCanonicalPriceText() {
        const quantityPrice = this.pricePerItem?.querySelector('span.price-per-item--current');
        if (quantityPrice && !quantityPrice.closest('.hidden')) {
          const text = quantityPrice.textContent.trim().replace(/\s+/g, ' ');
          if (text) return text;
        }

        const salePrice = this.priceRoot?.querySelector('.price--on-sale .price-item--last');
        if (salePrice) return salePrice.textContent.trim().replace(/\s+/g, ' ');

        const regularPrices = this.priceRoot?.querySelectorAll(
          '.price__regular .price-item--regular:not(.variant-item__old-price)'
        );
        return regularPrices?.length
          ? regularPrices[regularPrices.length - 1].textContent.trim().replace(/\s+/g, ' ')
          : '';
      }

      getCanonicalComparePriceText() {
        const comparePrice = this.priceRoot?.querySelector(
          '.price--on-sale .price__sale s.price-item--regular, '
          + '.volume-pricing--sale-badge .price__regular s.variant-item__old-price'
        );
        return comparePrice?.textContent.trim().replace(/\s+/g, ' ') || '';
      }

      getHandleOffer(currentPriceText = this.getCanonicalPriceText()) {
        const contextualCurrentMarker = this.priceRoot?.querySelector('[data-sticky-current-price]');
        const contextualCurrentPrice = contextualCurrentMarker?.textContent.trim().replace(/\s+/g, ' ');
        const lowestQuantityPrice = this.priceRoot
          ?.querySelector('[data-sticky-lowest-price]')
          ?.textContent.trim().replace(/\s+/g, ' ');

        return {
          isFrom: Boolean(
            lowestQuantityPrice || contextualCurrentMarker?.dataset?.stickyCurrentIsFrom === 'true'
          ),
          priceText: lowestQuantityPrice || currentPriceText || contextualCurrentPrice || '',
        };
      }

      updateVisibility() {
        const wasHidden = this.hidden;
        const supportedViewport = this.desktopQuery?.matches || this.mobileQuery?.matches;
        const navigationAllowsBuy = !this.sectionNavExpanded;
        const isEligible = Boolean(
          supportedViewport
          && this.canonicalForm
          && this.canonicalSubmit
          && this.canonicalSubmitPassed
          && navigationAllowsBuy
        );
        const shouldKeepFocused = Boolean(
          navigationAllowsBuy
          && supportedViewport
          && this.canonicalForm
          && this.canonicalSubmit
          && this.matches(':focus-within')
        );
        const shouldShow = isEligible || shouldKeepFocused;
        this.hidden = !shouldShow;
        this.setAttribute('aria-hidden', shouldShow ? 'false' : 'true');
        if (!shouldShow) this.setExpanded(false);
        if (wasHidden !== this.hidden) {
          this.dispatchEvent(new CustomEvent('product-sticky-buy:visibility', {
            bubbles: true,
            detail: { visible: shouldShow },
          }));
        }
      }
    }
  );
}
